import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { LoadingScreen } from '@dreebit-registry/vsm-microservice-components';
import IframeTroubleshootingLink from '../IframeTroubleshootingLink';

interface Props {
    src: string;
    title?: string;
    /** Receives the data of every other message posted by this iframe (origin and source checked). */
    onMessage?: (data: unknown) => void;
}

const AuthenticatedIframe = ({ src, title, onMessage }: Props) => {
    const iframeOrigin = new URL(src).origin;
    const iframeRef = useRef<HTMLIFrameElement>(null);
    // Kept in a ref so a new callback identity doesn't re-register the message listener.
    const onMessageRef = useRef(onMessage);
    onMessageRef.current = onMessage;
    const { keycloak } = useKeycloak();
    const [loading, setLoading] = useState(true);

    // React reuses this component instance when only the src changes (e.g. switching
    // from telemetry to maintenance), so the loading state must reset explicitly.
    useEffect(() => {
        setLoading(true);
    }, [src]);

    const postToken = useCallback(() => {
        const token = keycloak.token;
        console.log('[AuthIframe] postToken called, token exists:', !!token, 'length:', token?.length);
        iframeRef.current?.contentWindow?.postMessage(
            {
                type: 'KEYCLOAK_TOKEN',
                token,
                refreshToken: keycloak.refreshToken,
            },
            iframeOrigin
        );
        console.log('[AuthIframe] postMessage sent to', iframeOrigin);
    }, [keycloak.token, keycloak.refreshToken, iframeOrigin]);

    // Use native addEventListener for load — React's onLoad uses event delegation
    // which can miss load events on iframes (they don't bubble).
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        const handleLoad = () => {
            console.log('[AuthIframe] iframe load event fired');
            setLoading(false);
            postToken();
        };
        iframe.addEventListener('load', handleLoad);
        return () => iframe.removeEventListener('load', handleLoad);
    }, [postToken]);

    // Respond to KEYCLOAK_IFRAME_READY — the external app sends this signal as soon
    // as it mounts and is ready to receive the token. This is the primary mechanism
    // and eliminates all timing races between the iframe loading and the load event.
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // Log ALL messages to diagnose if KEYCLOAK_IFRAME_READY ever arrives
            if (event.origin === iframeOrigin) {
                console.log('[AuthIframe] message from iframe origin:', event.data?.type, event.data);
            } else {
                console.log('[AuthIframe] message from other origin:', event.origin, event.data?.type);
            }

            if (event.data?.type !== 'KEYCLOAK_IFRAME_READY') {
                // Other messages reach the parent only from this very frame and origin.
                if (event.source === iframeRef.current?.contentWindow && event.origin === iframeOrigin) {
                    onMessageRef.current?.(event.data);
                }
                return;
            }
            console.log('[AuthIframe] KEYCLOAK_IFRAME_READY received! source matches iframe?', event.source === iframeRef.current?.contentWindow);
            if (event.source !== iframeRef.current?.contentWindow) return;
            postToken();
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [postToken, iframeOrigin]);

    return (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <IframeTroubleshootingLink />
            <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex' }}>
                <iframe
                    ref={iframeRef}
                    src={src}
                    title={title}
                    style={{ flex: 1, width: '100%', border: 'none' }}
                />
                {loading && (
                    <LoadingScreen style={{ position: 'absolute', inset: 0, backgroundColor: '#fff' }} />
                )}
            </Box>
        </Box>
    );
};

export default AuthenticatedIframe;
